import { z } from 'zod';
import { PageQuerySchema } from './common';

export const BAC_SECTIONS = [
  'Mathématiques',
  'Sciences expérimentales',
  'Sciences techniques',
  'Informatique',
  'Économie et gestion',
  'Lettres',
  'Sport',
] as const;
export const BAC_SESSIONS = ['principale', 'controle'] as const;
export const PIECE_KINDS = ['cin', 'bac_transcript', 'bac_certificate', 'photo', 'fee_receipt'] as const;
export type PieceKind = (typeof PIECE_KINDS)[number];
/** nouv / etude / ent / acc / ref in the design. */
export const APPLICATION_STATUSES = [
  'draft',
  'submitted',
  'review',
  'interview',
  'accepted',
  'refused',
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];
export const PIECE_STATUSES = ['uploaded', 'verified', 'rejected'] as const;

export const PIECE_MAX_BYTES = 5 * 1024 * 1024;
export const PIECE_MIME = ['application/pdf', 'image/jpeg', 'image/png'] as const;

const name = z.string().trim().min(1, 'admissions.errors.required').max(80);
export const CinSchema = z
  .string()
  .trim()
  .regex(/^\d{8}$/, 'admissions.errors.cin');
export const EmailSchema = z.string().trim().toLowerCase().pipe(z.email('admissions.errors.email').max(120));
export const PhoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ]{8,16}$/, 'admissions.errors.phone');

/** Draft: every field optional (autosave), validated in full at submission. */
export const ApplicationDraftSchema = z.object({
  programId: z.uuid().nullable().optional(),
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  cin: z.string().trim().max(8).optional(),
  birthDate: z.iso.date().nullable().optional(),
  email: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(20).optional(),
  bacSection: z.enum(BAC_SECTIONS).nullable().optional(),
  bacSession: z.enum(BAC_SESSIONS).nullable().optional(),
  bacYear: z.number().int().min(2000).max(2100).nullable().optional(),
  bacAverage: z.number().min(0).max(20).nullable().optional(),
  highSchool: z.string().trim().max(120).optional(),
});
export type ApplicationDraft = z.infer<typeof ApplicationDraftSchema>;

/** Full validation at submission (same rules client- and server-side). */
export const ApplicationSubmitSchema = z.object({
  programId: z.uuid('admissions.errors.program'),
  firstName: name,
  lastName: name,
  cin: CinSchema,
  birthDate: z.iso.date('admissions.errors.birthDate'),
  email: EmailSchema,
  phone: PhoneSchema,
  bacSection: z.enum(BAC_SECTIONS, 'admissions.errors.required'),
  bacSession: z.enum(BAC_SESSIONS, 'admissions.errors.required'),
  bacYear: z.number().int().min(2000).max(2100),
  bacAverage: z
    .number('admissions.errors.average')
    .min(0, 'admissions.errors.average')
    .max(20, 'admissions.errors.average'),
  highSchool: z.string().trim().max(120).optional(),
});

export const PieceSchema = z.object({
  id: z.uuid(),
  kind: z.enum(PIECE_KINDS),
  fileName: z.string(),
  mime: z.string(),
  size: z.number().int(),
  status: z.enum(PIECE_STATUSES),
  rejectReason: z.string().nullable(),
  uploadedAt: z.string(),
});
export type Piece = z.infer<typeof PieceSchema>;

export const DraftViewSchema = ApplicationDraftSchema.extend({
  id: z.uuid(),
  status: z.enum(APPLICATION_STATUSES),
  pieces: z.array(PieceSchema),
  updatedAt: z.string(),
});
export type DraftView = z.infer<typeof DraftViewSchema>;

export const PublicAdmissionsSchema = z.object({
  open: z.boolean(),
  yearLabel: z.string().nullable(),
  applicationFeeMillimes: z.number().int(),
  responseDays: z.number().int(),
  requiredPieces: z.array(z.enum(PIECE_KINDS)),
  programs: z.array(
    z.object({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      nameAr: z.string().nullable(),
      cycle: z.enum(['ingenieur', 'licence', 'master']),
      durationYears: z.number().int(),
      capacity: z.number().int(),
      bacSections: z.array(z.enum(BAC_SECTIONS)),
    }),
  ),
});
export type PublicAdmissions = z.infer<typeof PublicAdmissionsSchema>;

export const SubmitResultSchema = z.object({
  tracking: z.string(),
  accessCode: z.string(),
  email: z.string(),
});

export const TrackRequestSchema = z.object({
  tracking: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^PRE-\d{4}-\d{4,}$/, 'admissions.errors.tracking'),
  /** Always 8 characters (`randomCode(8)`): a 9-character code is a typo, said before anything is sent. */
  accessCode: z.string().trim().toUpperCase().length(8, 'admissions.track.codeFormat'),
});

/** Candidate tracker (design: timeline done / current / todo). Only what the candidate already knows. */
export const TrackViewSchema = z.object({
  tracking: z.string(),
  firstName: z.string(),
  programName: z.string().nullable(),
  yearLabel: z.string().nullable(),
  status: z.enum(APPLICATION_STATUSES),
  submittedAt: z.string().nullable(),
  feeVerifiedAt: z.string().nullable(),
  interviewAt: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionReason: z.string().nullable(),
  responseDays: z.number().int(),
  pieces: z.array(PieceSchema),
  requiredPieces: z.array(z.enum(PIECE_KINDS)),
  /** Missing or rejected pieces can be (re)uploaded while the file is under review. */
  canUpload: z.boolean(),
});
export type TrackView = z.infer<typeof TrackViewSchema>;

// ── Staff ─────────────────────────────────────────────────────────────────────
export const ApplicationListQuerySchema = PageQuerySchema.extend({
  status: z.enum(APPLICATION_STATUSES).optional(),
  programId: z.uuid().optional(),
  since: z.iso.date().optional(),
});
export type ApplicationListQuery = z.infer<typeof ApplicationListQuerySchema>;

export const ApplicationRowSchema = z.object({
  id: z.uuid(),
  tracking: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  programId: z.uuid().nullable(),
  programName: z.string().nullable(),
  bacSection: z.string().nullable(),
  bacAverage: z.number().nullable(),
  submittedAt: z.string().nullable(),
  piecesUploaded: z.number().int(),
  piecesRequired: z.number().int(),
  status: z.enum(APPLICATION_STATUSES),
});
export type ApplicationRow = z.infer<typeof ApplicationRowSchema>;

export const ApplicationPageSchema = z.object({
  items: z.array(ApplicationRowSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** Per status, for the tabs (same filters, all statuses). */
  counts: z.record(z.string(), z.number().int()),
});

export const ApplicationDetailSchema = ApplicationRowSchema.extend({
  cin: z.string().nullable(),
  birthDate: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  bacSession: z.string().nullable(),
  bacYear: z.number().int().nullable(),
  highSchool: z.string().nullable(),
  yearLabel: z.string().nullable(),
  interviewAt: z.string().nullable(),
  decisionReason: z.string().nullable(),
  studentId: z.uuid().nullable(),
  pieces: z.array(PieceSchema),
  requiredPieces: z.array(z.enum(PIECE_KINDS)),
  history: z.array(
    z.object({
      id: z.uuid(),
      action: z.string(),
      at: z.string(),
      actorName: z.string().nullable(),
      data: z.record(z.string(), z.string()),
    }),
  ),
});
export type ApplicationDetail = z.infer<typeof ApplicationDetailSchema>;

export const BULK_ACTIONS = ['review', 'accept', 'interview', 'refuse'] as const;
export const BulkActionSchema = z
  .object({
    ids: z.array(z.uuid()).min(1).max(200),
    action: z.enum(BULK_ACTIONS),
    reason: z.string().trim().max(500).optional(),
    /** Interview date-time (ISO, local time of the school). */
    interviewAt: z.iso.datetime({ offset: true, local: true }).optional(),
  })
  .refine((b) => b.action !== 'interview' || Boolean(b.interviewAt), {
    message: 'admissions.errors.interviewAt',
    path: ['interviewAt'],
  });
export type BulkAction = z.infer<typeof BulkActionSchema>;

export const BulkResultSchema = z.object({
  done: z.array(z.uuid()),
  skipped: z.array(z.object({ id: z.uuid(), reason: z.string() })),
});

export const PieceDecisionSchema = z
  .object({
    verified: z.boolean(),
    reason: z.string().trim().min(3, 'admissions.errors.reason').max(300).optional(),
  })
  .refine((d) => d.verified || Boolean(d.reason), { message: 'admissions.errors.reason', path: ['reason'] });

export const ConvertResultSchema = z.object({
  studentId: z.uuid(),
  matricule: z.string(),
  created: z.boolean(),
});

export const AdmissionsOverviewSchema = z.object({
  yearLabel: z.string().nullable(),
  open: z.boolean(),
  drafts: z.number().int(),
  byStatus: z.record(z.string(), z.number().int()),
  programs: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      capacity: z.number().int(),
      submitted: z.number().int(),
      accepted: z.number().int(),
    }),
  ),
});
export type AdmissionsOverview = z.infer<typeof AdmissionsOverviewSchema>;
